import { memo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import holoBg from '../../assets/figma/holo-bg.png';
import { dbgFlag } from '../../lib/debug';

type Props = {
  /** Height of the glow band in px (default 400). */
  height?: number;
  /** Where the fade-out begins, as a % of height (default 80 → late fade). */
  fadeStart?: number;
  /** Cover the whole screen with the holographic image (no fade). */
  full?: boolean;
  /** Full mode also paints a copy inside the screen for overlay shells.
   *  Tabs that are already transparent should pass false — the viewport
   *  copy is the only one, otherwise the two crops read as two backgrounds. */
  inTree?: boolean;
  /** Colour that continues under the nav / home-indicator / iOS dead band.
   *  Defaults to the holo canvas in full mode, otherwise the app surface. */
  canvas?: string;
};

/** The colour the blurred holo actually has where the page ends.
 *
 *  Used for html / body / the home-indicator fill / Telegram's own bottom bar —
 *  native chrome we cannot draw the image into, only tint, so it has to match.
 *
 *  Measured, not picked: holo-bg.png is square and the container is portrait,
 *  so `object-cover` always maps the full image height onto the container and
 *  `scale(1.8)` always leaves the middle 55.6% visible. The bottom edge is
 *  therefore row 63.9% on every phone size, and averaging the visible columns
 *  there gives this, stable across blur radii. The previous value was a warm
 *  cream (#f5f7ea), which is why the strip under the cards screen read as a
 *  band instead of disappearing into the page. */
const HOLO_CANVAS = '#e2eaf4';
const SURFACE = '#eeeff4';

/** Soft holographic glow anchored to the top of a screen.
 *
 *  Partial bands stay in-tree; safe-area strips are portaled to body (WebKit
 *  clips in-tree fixed overflow to the safe-area).
 *
 *  Full-screen mode uses both:
 *  - a viewport-fixed portal behind #root (scrollable tabs keep the glow)
 *  - an in-tree absolute layer (overlays with `bg-holo` still show the image)
 *  plus html/body / TG bottom-bar tint to the holo canvas colour. */
export const ScreenBackdrop = memo(function ScreenBackdrop({
  height = 400,
  fadeStart = 80,
  full,
  inTree = true,
  canvas,
}: Props) {
  // Safe-mode bisect: the blurred, 1.8x-upscaled holo-bg.png is a large GPU
  // buffer. Skip it entirely when "card images" is on (see CardBg / main.tsx).
  const skip = dbgFlag('noimg');
  const canvasColor = canvas ?? (full ? HOLO_CANVAS : SURFACE);

  useEffect(() => {
    if (skip) return;
    const html = document.documentElement;
    const body = document.body;
    const fill = document.getElementById('home-indicator-fill');
    const themeMetas = [
      ...document.querySelectorAll('meta[name="theme-color"]'),
    ] as HTMLMetaElement[];
    const prevHtml = html.style.backgroundColor;
    const prevBody = body.style.backgroundColor;
    const prevHtmlImg = html.style.backgroundImage;
    const prevHtmlSize = html.style.backgroundSize;
    const prevHtmlPos = html.style.backgroundPosition;
    const prevHtmlRepeat = html.style.backgroundRepeat;
    const prevBodyImg = body.style.backgroundImage;
    const prevBodySize = body.style.backgroundSize;
    const prevBodyPos = body.style.backgroundPosition;
    const prevBodyRepeat = body.style.backgroundRepeat;
    const prevCanvas = html.style.getPropertyValue('--app-canvas');
    const prevFill = fill?.style.backgroundColor ?? '';
    const prevTgBottom = html.getAttribute('data-tg-bottom-bar');
    const prevThemes = themeMetas.map((m) => m.content);

    html.style.backgroundColor = canvasColor;
    body.style.backgroundColor = canvasColor;
    html.style.setProperty('--app-canvas', canvasColor);
    // On standalone, html/body are sized to screen.height so their background
    // paints the status-bar strip AND the bottom dead band together. #root /
    // the tab bar stay on the layout viewport. Full screens use the holo
    // asset; partial screens use the flat canvas tint.
    const applyBg = (el: HTMLElement, image: boolean) => {
      if (image) {
        el.style.backgroundImage = `url(${holoBg})`;
        // `cover` would scale this to the element's CURRENT height, and
        // Telegram shrinks the webview when the keyboard opens — so the crop,
        // and the colour, would change under the user mid-typing. `auto
        // <stable height>` is the same cover behaviour for a portrait box
        // (a square image covers by height, overflowing sideways), with the
        // height pinned to the measurement that ignores the keyboard.
        el.style.backgroundSize = 'auto var(--tg-viewport-stable-height)';
        el.style.backgroundPosition = 'center top';
        el.style.backgroundRepeat = 'no-repeat';
      } else {
        el.style.backgroundImage = '';
        el.style.backgroundSize = '';
        el.style.backgroundPosition = '';
        el.style.backgroundRepeat = '';
      }
    };
    if (full) {
      applyBg(html, true);
      applyBg(body, true);
      for (const m of themeMetas) m.content = HOLO_CANVAS;
    } else {
      applyBg(html, false);
      applyBg(body, false);
      for (const m of themeMetas) m.content = canvasColor;
    }
    if (fill) fill.style.backgroundColor = 'transparent';
    html.setAttribute('data-tg-bottom-bar', canvasColor);
    const wa = window.Telegram?.WebApp;
    wa?.setBottomBarColor?.(canvasColor);
    return () => {
      html.style.backgroundColor = prevHtml;
      body.style.backgroundColor = prevBody;
      html.style.backgroundImage = prevHtmlImg;
      html.style.backgroundSize = prevHtmlSize;
      html.style.backgroundPosition = prevHtmlPos;
      html.style.backgroundRepeat = prevHtmlRepeat;
      body.style.backgroundImage = prevBodyImg;
      body.style.backgroundSize = prevBodySize;
      body.style.backgroundPosition = prevBodyPos;
      body.style.backgroundRepeat = prevBodyRepeat;
      if (prevCanvas) html.style.setProperty('--app-canvas', prevCanvas);
      else html.style.removeProperty('--app-canvas');
      themeMetas.forEach((m, i) => {
        m.content = prevThemes[i] ?? SURFACE;
      });
      if (fill) fill.style.backgroundColor = prevFill;
      if (prevTgBottom == null) html.removeAttribute('data-tg-bottom-bar');
      else html.setAttribute('data-tg-bottom-bar', prevTgBottom);
      wa?.setBottomBarColor?.(prevTgBottom ?? SURFACE);
    };
  }, [canvasColor, full, skip]);

  if (skip) return null;

  const blur = full ? 20 : 15;
  const mask = full
    ? undefined
    : `linear-gradient(to bottom, black 0%, black ${fadeStart}%, transparent 100%)`;

  if (typeof document === 'undefined') return null;

  if (full) {
    return (
      <>
        {createPortal(
          <>
            {/* One full-screen crop — no top bleed (that was a second holo). */}
            <div aria-hidden className="screen-backdrop-full">
              <img
                src={holoBg}
                alt=""
                className="h-full w-full scale-[1.8] object-cover"
                style={{ filter: `blur(${blur}px)` }}
              />
            </div>
            {/* Same crop, bottom-anchored — WebKit clips the full layer away
                from the home-indicator strip inside the layout viewport. */}
            <div aria-hidden className="screen-backdrop-bleed-bottom">
              <img
                src={holoBg}
                alt=""
                className="absolute bottom-0 left-0 w-full scale-[1.8] object-cover"
                style={{
                  // The same stable height the full layer uses, so this strip
                  // shows the same slice of the picture instead of its own —
                  // and so the keyboard cannot re-crop it either.
                  height: 'var(--tg-viewport-stable-height)',
                  filter: `blur(${blur}px)`,
                }}
              />
            </div>
          </>,
          document.body,
        )}
        {/* In-tree: paints above opaque overlay shells (`bg-holo`). Portal alone
            sits behind #root and cannot cover a fixed push layer. */}
        {inTree && (
          <div aria-hidden className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
            <img
              src={holoBg}
              alt=""
              className="w-full scale-[1.8] object-cover"
              style={{
                // Not h-full: this layer's box is the screen shell, which
                // Telegram shrinks when the keyboard opens. A cover crop
                // measured against a shrinking box paints a different slice
                // of the picture, so the background changes colour mid-typing.
                // Pinned to the same stable height as the portal, the two
                // layers also agree with each other rather than drifting
                // apart on a screen that scrolls.
                height: 'var(--tg-viewport-stable-height)',
                filter: `blur(${blur}px)`,
              }}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {createPortal(
        <>
          <div aria-hidden className="screen-backdrop-bleed">
            <img
              src={holoBg}
              alt=""
              className="h-[100lvh] w-full origin-top scale-[1.8] object-cover"
              style={{ filter: `blur(${blur}px)` }}
            />
          </div>
          <div aria-hidden className="screen-backdrop-bleed-bottom">
            <div className="h-full w-full" style={{ backgroundColor: canvasColor }} />
          </div>
        </>,
        document.body,
      )}
      <div aria-hidden className="screen-backdrop" style={{ height }}>
        <img
          src={holoBg}
          alt=""
          className="h-full w-full scale-[1.8] object-cover"
          style={{
            filter: `blur(${blur}px)`,
            maskImage: mask,
            WebkitMaskImage: mask,
          }}
        />
      </div>
    </>
  );
});
